import { track } from "@/lib/analytics";
import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { ChevronRight, Plus, Trash2, Download, Pencil, X, Check, ChevronDown, ChevronUp, Receipt, Wallet } from "lucide-react";
import { toast } from "sonner";
import { escapeDeep, todayIST } from "@/lib/utils";
import axios from "axios";
import { can } from "@/lib/permissions";
import { VendorPaymentModal } from "./payments/ledgerShared";
import { computeDocTaxes, hasOwnRate, lineRate, compositeLines, TAX_TREATMENTS } from "@/lib/gst";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GST_RATES = [0, 5, 12, 18, 28];
const UNITS = ["Nos", "Set", "Lot", "m", "Rm", "kg", "MT", "Wp", "kWp", "sq.m", "litre", "service"];
const fmtAmt = (n) => `₹${(Number(n) || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
/** Bill-to / ship-to / GSTIN for a new PI: the client master when the project's client is
 *  saved there, otherwise the project's own client fields — as real values, not placeholders. */
function clientPrefill(project, masters) {
  if (!project) return {};
  const cname = (project.client_name || "").trim();
  const m = cname ? (masters || []).find(x => (x.name || "").trim().toLowerCase() === cname.toLowerCase()) : null;
  const addr = (m?.billing_address || "").trim();
  const bill_to = addr && cname && addr.toLowerCase().startsWith(cname.toLowerCase()) ? addr : [m?.name || cname, addr].filter(Boolean).join("\n");
  return {
    bill_to,
    ship_to: m?.ship_to_address || project.location || "",
    client_gstin: m?.gstin || "",
    project_reference: project.name || "",
  };
}

function LineItemsTable({ items, setItems, readOnly = false, defaultRate = 18, catalogue = [] }) {
  const updateItem = (idx, field, value) => {
    const newItems = [...items];
    newItems[idx] = { ...newItems[idx], [field]: value };
    if (field === "description") {
      // Exact match on a catalogue item → fill HSN, unit, last rate and GST from it
      // Exact match on a catalogue item → fill only what is still blank (never overwrite a typed rate)
      const hit = catalogue.find(c => (c.description || "").toLowerCase() === String(value).trim().toLowerCase());
      if (hit) {
        const cur = newItems[idx];
        newItems[idx] = { ...cur, description: hit.description, hsn_code: cur.hsn_code || hit.hsn_code || "", unit: cur.unit && cur.unit !== "Nos" ? cur.unit : (hit.unit || "Nos"),
          rate: (parseFloat(cur.rate) || 0) > 0 ? cur.rate : (hit.rate ?? cur.rate), gst_rate: hasOwnRate(cur) ? cur.gst_rate : (hit.gst_rate ?? null) };
      }
    }
    if (["quantity", "rate", "description"].includes(field)) {
      newItems[idx].amount = (parseFloat(newItems[idx].quantity) || 0) * (parseFloat(newItems[idx].rate) || 0);
    }
    setItems(newItems);
  };
  const addItem = () => setItems([...items, { description: "", hsn_code: "", quantity: 1, unit: "Nos", rate: 0, amount: 0, gst_rate: null }]);
  const removeItem = (idx) => setItems(items.filter((_, i) => i !== idx));

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="bg-[#F5F8F5] text-[#1B2B22]">
            <th className="px-1.5 py-2 text-left">#</th>
            <th className="px-1.5 py-2 text-left">Description</th>
            <th className="px-1.5 py-2 text-left">HSN</th>
            <th className="px-1.5 py-2 text-left">Qty</th>
            <th className="px-1.5 py-2 text-left">Unit</th>
            <th className="px-1.5 py-2 text-right">Rate (₹)</th>
            <th className="px-1.5 py-2 text-left" title="Per-line GST rate; “default” uses the document rate below">GST %</th>
            <th className="px-1.5 py-2 text-right">Amount (₹)</th>
            {!readOnly && <th className="px-1.5 py-2"></th>}
          </tr>
        </thead>
        <tbody>
          {items.map((item, i) => (
            <tr key={item.id || `item-${i}`} className={i % 2 === 0 ? "bg-white" : "bg-[#F8FAFC]"}>
              <td className="px-1.5 py-2 text-[#64748B]">{i + 1}</td>
              <td className="px-1.5 py-2">
                {readOnly ? item.description : (
                  <input type="text" list="item-catalogue" value={item.description} onChange={e => updateItem(i, "description", e.target.value)} placeholder={catalogue.length ? "Type or pick a saved item" : "Description"} className="w-full border border-[#E2E8F0] rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-[#FFB703] min-w-32" data-testid={`li-desc-${i}`} />
                )}
              </td>
              <td className="px-1.5 py-2">
                {readOnly ? item.hsn_code : (
                  <>
                  <input type="text" list="hsn-codes" value={item.hsn_code || ""} onChange={e => updateItem(i, "hsn_code", e.target.value)} className="w-14 border border-[#E2E8F0] rounded px-1.5 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-[#FFB703]" placeholder="HSN" />
                  <datalist id="hsn-codes">
                    <option value="8541">8541 — Solar modules/cells</option>
                    <option value="850440">850440 — Inverters</option>
                    <option value="854460">854460 — Cable</option>
                    <option value="761090">761090 — Mounting structure</option>
                    <option value="9954">9954 — EPC / installation services</option>
                  </datalist>
                  </>
                )}
              </td>
              <td className="px-1.5 py-2">
                {readOnly ? item.quantity : (
                  <input type="number" value={item.quantity} onChange={e => updateItem(i, "quantity", e.target.value)} className="w-14 border border-[#E2E8F0] rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-[#FFB703]" min="0" />
                )}
              </td>
              <td className="px-1.5 py-2">
                {readOnly ? item.unit : (
                  <select value={item.unit} onChange={e => updateItem(i, "unit", e.target.value)} className="w-16 border border-[#E2E8F0] rounded px-1 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-[#FFB703] bg-white">
                    {UNITS.map(u => <option key={u}>{u}</option>)}
                  </select>
                )}
              </td>
              <td className="px-1.5 py-2">
                {readOnly ? `₹${(item.rate || 0).toLocaleString("en-IN")}` : (
                  <input type="number" value={item.rate} onChange={e => updateItem(i, "rate", e.target.value)} className="w-20 border border-[#E2E8F0] rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-[#FFB703] text-right" min="0" />
                )}
              </td>
              <td className="px-1.5 py-2">
                {readOnly ? `${lineRate(item, defaultRate)}%` : (
                  <select value={hasOwnRate(item) ? String(item.gst_rate) : ""} onChange={e => updateItem(i, "gst_rate", e.target.value === "" ? null : +e.target.value)}
                    className={`border rounded px-1 py-1 text-[11px] w-[4.6rem] focus:outline-none focus:ring-1 focus:ring-[#FFB703] bg-white ${hasOwnRate(item) && lineRate(item, defaultRate) !== defaultRate ? "border-[#2F7D52] text-[#2F7D52] font-semibold" : "border-[#E2E8F0]"}`} data-testid={`li-gst-${i}`}>
                    <option value="">{defaultRate}% (doc)</option>
                    {GST_RATES.map(r => <option key={r} value={r}>{r}%</option>)}
                  </select>
                )}
              </td>
              <td className="px-1.5 py-2 text-right font-semibold whitespace-nowrap tabular-nums">₹{(item.amount || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}</td>
              {!readOnly && (
                <td className="px-1.5 py-2">
                  <button onClick={() => removeItem(i)} className="text-red-400 hover:text-red-600">
                    <Trash2 size={12} />
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      {!readOnly && catalogue.length > 0 && (
        <datalist id="item-catalogue">
          {catalogue.map(c => <option key={c.id} value={c.description}>{[c.hsn_code, c.rate != null ? `₹${Number(c.rate).toLocaleString("en-IN")}/${c.unit || "Nos"}` : null, c.gst_rate != null ? `GST ${c.gst_rate}%` : null].filter(Boolean).join(" · ")}</option>)}
        </datalist>
      )}
      {!readOnly && (
        <div className="mt-2 flex flex-wrap items-center gap-3">
          <button onClick={addItem} className="flex items-center gap-1 text-xs text-[#FFB703] hover:text-[#E8A200] font-medium">
            <Plus size={12} />Add Item
          </button>
          <WpQuickAdd items={items} setItems={setItems} />
          {catalogue.length > 0 && <span className="text-[10px] text-[#2F7D52]">{catalogue.length} saved item{catalogue.length === 1 ? "" : "s"} — start typing a description to pick one</span>}
        </div>
      )}
    </div>
  );
}

// ₹/Wp pricing helper: capacity (kWp) × rate (₹/Wp) → adds a line with amount = kWp × 1000 × ₹/Wp
function WpQuickAdd({ items, setItems }) {
  const [kwp, setKwp] = useState("");
  const [rate, setRate] = useState("");
  const add = () => {
    const cap = parseFloat(kwp) || 0;
    const rw = parseFloat(rate) || 0;
    if (cap <= 0 || rw <= 0) { toast.error("Enter capacity (kWp) and rate (₹/Wp)"); return; }
    const ratePerKwp = rw * 1000; // ₹/Wp → ₹/kWp
    const line = { description: `Solar PV supply & installation — ${cap} kWp @ ₹${rw}/Wp`, hsn_code: "9954", quantity: cap, unit: "kWp", rate: ratePerKwp, amount: cap * ratePerKwp, gst_rate: null };
    setItems([...items.filter(it => it.description || it.quantity), line]);
    setKwp(""); setRate("");
    toast.success(`Added ${cap} kWp line (₹${(cap * ratePerKwp).toLocaleString("en-IN")})`);
  };
  return (
    <div className="flex items-center gap-1.5 text-xs bg-[#F5F8F5] rounded px-2 py-1 border border-[#E2E8F0]" data-testid="wp-pricing-helper">
      <span className="text-[#64748B] font-medium">₹/Wp:</span>
      <input type="number" value={kwp} onChange={e => setKwp(e.target.value)} placeholder="kWp" className="w-16 border border-[#E2E8F0] rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-[#2F7D52]" data-testid="wp-kwp-input" />
      <span className="text-[#64748B]">×</span>
      <input type="number" value={rate} onChange={e => setRate(e.target.value)} placeholder="₹/Wp" className="w-16 border border-[#E2E8F0] rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-[#2F7D52]" data-testid="wp-rate-input" />
      <button onClick={add} className="text-[#2F7D52] font-semibold hover:underline" data-testid="wp-add-btn">Add line</button>
    </div>
  );
}

// Utility: open HTML in a new tab using Blob URL (bypasses popup blockers)
function openHtmlInNewTab(html) {
  const blob = new Blob([html], { type: "text/html" });
  const url = URL.createObjectURL(blob);
  const win = window.open(url, "_blank");
  if (!win) {
    toast.error("Please allow popups for this site to open print preview");
  }
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

export default function FinancialDocPage({ type }) {
  const { id: projectId } = useParams();
  const milestoneParam = new URLSearchParams(window.location.search).get("milestone");
  const [milestoneDraft, setMilestoneDraft] = useState(null);
  const { workspace, user } = useAuth();
  const canEditDoc = can(user, type === "po" ? "po.edit" : "pi.edit");
  const canVoidDoc = can(user, type === "po" ? "po.void" : "pi.void");
  const canMarkPaid = can(user, "client_payments.edit");
  const [project, setProject] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [masters, setMasters] = useState([]);
  const [catalogue, setCatalogue] = useState([]);
  const [payPo, setPayPo] = useState(null);          // PO being paid from the list (VendorPaymentModal)
  const canPayVendor = can(user, "vendor_payments.edit");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Edit mode state
  const [editDocId, setEditDocId] = useState(null);
  const [expandedDocId, setExpandedDocId] = useState(null);

  const isPO = type === "po";
  const [gstRate, setGstRate] = useState(18);
  const [taxMode, setTaxMode] = useState("auto");

  const detectTaxMode = (vendorGstin, wsGstin) => {
    if (!vendorGstin || !wsGstin || vendorGstin.length < 2 || wsGstin.length < 2) return "cgst_sgst";
    return vendorGstin.slice(0, 2).toUpperCase() === wsGstin.slice(0, 2).toUpperCase() ? "cgst_sgst" : "igst";
  };

  const updateTaxRates = (newTaxMode, newGstRate) => {
    const mode = newTaxMode || taxMode;
    const rate = newGstRate !== undefined ? newGstRate : gstRate;
    if (mode === "cgst_sgst") {
      setForm(p => ({ ...p, cgst_rate: rate / 2, sgst_rate: rate / 2, igst_rate: 0 }));
    } else {
      setForm(p => ({ ...p, cgst_rate: 0, sgst_rate: 0, igst_rate: rate }));
    }
  };

  const handleVendorGstinChange = (e) => {
    const vendorGstin = e.target.value;
    setForm(p => ({ ...p, vendor_gstin: vendorGstin }));
    if (taxMode === "auto") {
      const wsGstin = workspace?.gstin || "";
      const detected = detectTaxMode(vendorGstin, wsGstin);
      updateTaxRates(detected, gstRate);
    }
  };

  const blankPOForm = () => ({
    split_mode: "auto",
    vendor_name: "", vendor_address: "", vendor_gstin: "", date: todayIST(),
    items: [{ description: "", hsn_code: "", quantity: 1, unit: "Nos", rate: 0, amount: 0, gst_rate: null }],
    cgst_rate: 9, sgst_rate: 9, igst_rate: 0, payment_terms: "", delivery_terms: "", notes: ""
  });

  // New PIs start with the project's customer filled in (real values, editable) — see clientPrefill.
  const blankPIForm = (proj = project, mst = masters) => ({
    tax_treatment: "services_18", split_mode: "auto",
    bill_to: "", ship_to: "", client_gstin: "", project_reference: "", milestone: "",
    date: todayIST(),
    items: [{ description: "", hsn_code: "", quantity: 1, unit: "Nos", rate: 0, amount: 0, gst_rate: null }],
    cgst_rate: 9, sgst_rate: 9, igst_rate: 0, tds_applicable: false, tds_rate: 2,
    terms_conditions: "", notes: "", is_shared_with_client: false,
    ...clientPrefill(proj, mst),
  });

  const [form, setForm] = useState(isPO ? blankPOForm() : blankPIForm(null, []));

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [pRes, docsRes] = await Promise.all([
          axios.get(`${API}/projects/${projectId}`),
          axios.get(`${API}/financials/${isPO ? "po" : "pi"}?project_id=${projectId}`)
        ]);
        setProject(pRes.data);
        setDocuments(docsRes.data);
        let mst = [];
        try {
          const mRes = await axios.get(`${API}/masters/${isPO ? "vendors" : "clients"}`);
          mst = mRes.data || [];
          setMasters(mst);
        } catch (e) { /* masters optional */ }
        try {
          const cRes = await axios.get(`${API}/masters/items`, { params: { kind: isPO ? "po" : "pi" } });
          setCatalogue(cRes.data || []);
        } catch (e) { /* catalogue optional */ }
        if (!isPO) {
          const pre = clientPrefill(pRes.data, mst);
          setForm(p => ({ ...p, ...Object.fromEntries(Object.entries(pre).filter(([k, v]) => v && !p[k])) }));
          // Client GSTIN decides intra/inter-state; match the default split to it
          if (pre.client_gstin && workspace?.gstin) {
            const same = pre.client_gstin.slice(0, 2) === workspace.gstin.slice(0, 2);
            setForm(p => same ? { ...p, cgst_rate: gstRate / 2, sgst_rate: gstRate / 2, igst_rate: 0 } : { ...p, cgst_rate: 0, sgst_rate: 0, igst_rate: gstRate });
          }
        }
        if (!isPO && milestoneParam) {
          try {
            const { data: d } = await axios.get(`${API}/projects/${projectId}/milestones/${milestoneParam}/pi-draft`);
            setMilestoneDraft(d);
            setForm(p => ({ ...p, bill_to: d.bill_to || p.bill_to, ship_to: d.ship_to || p.ship_to, client_gstin: d.client_gstin || p.client_gstin,
              project_reference: d.project_reference || p.project_reference, milestone: d.milestone, items: d.items }));
          } catch (e) { toast.error(e.response?.data?.detail || "Could not pre-fill from the milestone"); }
        }
      } catch (err) { console.error(err); }
      setLoading(false);
    };
    fetchData();
    // load-once per project/doc type: the prefill reads the initial gstRate / workspace on purpose
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, isPO]);

  const isComposite = !isPO && form.tax_treatment === "solar_composite_70_30";
  const taxLines = isComposite ? compositeLines(form.items) : form.items;
  const taxes = computeDocTaxes(taxLines, form);
  const { subtotal, cgst, sgst, igst } = taxes;
  const tds = (form.tds_applicable && !isPO) ? subtotal * (form.tds_rate || 0) / 100 : 0;
  const total = subtotal + cgst + sgst + igst - tds;

  const startEdit = (doc) => {
    setEditDocId(doc.id);
    setTaxMode(doc.split_mode || "auto");
    if (isPO) {
      setForm({
        vendor_name: doc.vendor_name || "",
        vendor_address: doc.vendor_address || "",
        vendor_gstin: doc.vendor_gstin || "",
        date: doc.date || todayIST(),
        items: doc.items || [],
        tax_treatment: doc.tax_treatment || "services_18", split_mode: doc.split_mode || "auto",
        cgst_rate: doc.cgst_rate || 0,
        sgst_rate: doc.sgst_rate || 0,
        igst_rate: doc.igst_rate || 0,
        payment_terms: doc.payment_terms || "",
        delivery_terms: doc.delivery_terms || "",
        notes: doc.notes || "",
      });
      const detectedRate = (doc.cgst_rate || 0) * 2 || (doc.igst_rate || 0) || 18;
      setGstRate(detectedRate);
    } else {
      setForm({
        bill_to: doc.bill_to || "",
        ship_to: doc.ship_to || "",
        client_gstin: doc.client_gstin || "",
        project_reference: doc.project_reference || "",
        milestone: doc.milestone || "",
        date: doc.date || todayIST(),
        items: doc.items || [],
        cgst_rate: doc.cgst_rate || 0,
        sgst_rate: doc.sgst_rate || 0,
        igst_rate: doc.igst_rate || 0,
        tds_applicable: doc.tds_applicable || false,
        tds_rate: doc.tds_rate || 2,
        terms_conditions: doc.terms_conditions || "",
        notes: doc.notes || "",
        is_shared_with_client: doc.is_shared_with_client || false,
      });
      setGstRate((doc.cgst_rate || 0) * 2 || (doc.igst_rate || 0) || 18);
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelEdit = () => {
    setEditDocId(null);
    setForm(isPO ? blankPOForm() : blankPIForm());
  };

  const handleSave = async () => {
    if (!form.items.some(i => i.description && i.quantity > 0)) {
      toast.error("Add at least one valid line item");
      return;
    }
    const missingHsn = form.items.filter(i => i.description && i.quantity > 0 && !(i.hsn_code || "").trim());
    if (missingHsn.length) {
      toast.error("HSN/SAC code is required on every line item");
      return;
    }
    setSaving(true);
    const payload = {
      project_id: projectId,
      ...(milestoneDraft && !editDocId ? { milestone_id: milestoneDraft.milestone_id } : {}),
      ...form,
      split_mode: taxMode,
      items: form.items.map(i => ({
        ...i,
        quantity: parseFloat(i.quantity) || 0,
        rate: parseFloat(i.rate) || 0,
        amount: (parseFloat(i.quantity) || 0) * (parseFloat(i.rate) || 0),
        gst_rate: hasOwnRate(i) ? parseFloat(i.gst_rate) : null,
      }))
    };
    try {
      if (editDocId) {
        // Update existing
        const { data } = await axios.put(`${API}/financials/${isPO ? "po" : "pi"}/${editDocId}`, payload);
        setDocuments(prev => prev.map(d => d.id === editDocId ? data : d));
        toast.success(`${isPO ? "Purchase Order" : "Proforma Invoice"} updated!`);
        setEditDocId(null);
        setForm(isPO ? blankPOForm() : blankPIForm());
        refreshCatalogue();
      } else {
        // Create new
        const { data } = await axios.post(`${API}/financials/${isPO ? "po" : "pi"}`, payload);
        setDocuments(prev => [data, ...prev]);
        track(isPO ? "po_created" : "pi_created", { value: Number(data.total_amount || data.total || 0), currency: "INR", items: (data.items || []).length, from_milestone: !!milestoneDraft });
        toast.success(milestoneDraft ? `Invoice ${data.pi_number} raised for milestone "${milestoneDraft.milestone}"` : `${isPO ? "Purchase Order" : "Proforma Invoice"} created!`);
        upsertMaster();
        if (milestoneDraft) { setMilestoneDraft(null); window.history.replaceState(null, "", window.location.pathname); }
        setForm(isPO ? blankPOForm() : blankPIForm());
      }
    } catch (err) {
      if (err.response?.status === 409) {
        const proceed = window.confirm(`${err.response.data.detail}\n\nCreate it anyway?`);
        if (proceed) {
          try {
            const { data } = await axios.post(`${API}/financials/${isPO ? "po" : "pi"}`, { ...payload, confirm_duplicate: true });
            setDocuments(prev => [data, ...prev]);
            track(isPO ? "po_created" : "pi_created", { value: Number(data.total_amount || data.total || 0), currency: "INR", items: (data.items || []).length, duplicate_confirmed: true });
            toast.success(`${isPO ? "Purchase Order" : "Proforma Invoice"} created!`);
            upsertMaster();
            if (milestoneDraft) { setMilestoneDraft(null); window.history.replaceState(null, "", window.location.pathname); }
            setForm(isPO ? blankPOForm() : blankPIForm());
          } catch (e2) {
            toast.error(e2.response?.data?.detail || "Failed to save document");
          }
        }
        setSaving(false);
        return;
      }
      toast.error(err.response?.data?.detail || "Failed to save document");
    }
    setSaving(false);
  };

  const refreshCatalogue = async () => {
    try { const { data } = await axios.get(`${API}/masters/items`, { params: { kind: isPO ? "po" : "pi" } }); setCatalogue(data || []); } catch (e) { /* optional */ }
  };

  // Grow the vendor/client master from what the user just entered (dedup handled server-side)
  const upsertMaster = async () => {
    refreshCatalogue();
    try {
      if (isPO && form.vendor_name?.trim()) {
        const { data } = await axios.post(`${API}/masters/vendors`, {
          name: form.vendor_name, gstin: form.vendor_gstin, address: form.vendor_address, default_payment_terms: form.payment_terms,
        });
        setMasters(prev => prev.some(m => m.id === data.id) ? prev : [...prev, data]);
      } else if (!isPO && form.bill_to?.trim()) {
        const name = form.bill_to.split("\n")[0].trim();
        const { data } = await axios.post(`${API}/masters/clients`, {
          name, gstin: form.client_gstin, billing_address: form.bill_to, ship_to_address: form.ship_to,
        });
        setMasters(prev => prev.some(m => m.id === data.id) ? prev : [...prev, data]);
      }
    } catch (e) { /* non-blocking */ }
  };

  const voidDoc = async (doc) => {
    const reason = window.prompt(`Void ${doc[isPO ? "po_number" : "pi_number"]}? This keeps the number in sequence but excludes it from all totals.\n\nReason:`);
    if (reason === null) return;
    if (!reason.trim()) { toast.error("A reason is required to void."); return; }
    try {
      const { data } = await axios.post(`${API}/financials/${isPO ? "po" : "pi"}/${doc.id}/void`, { reason: reason.trim() });
      setDocuments(prev => prev.map(d => d.id === doc.id ? data : d));
      toast.success(`${doc[isPO ? "po_number" : "pi_number"]} voided`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not void document");
    }
  };

  const refreshPo = async (poId) => {
    try { const { data } = await axios.get(`${API}/financials/po/${poId}`); setDocuments(p => p.map(d => d.id === poId ? data : d)); } catch (e) { /* list refresh is best-effort */ }
  };
  const poBalance = (doc) => Math.max(0, +(doc.balance_due ?? ((doc.total || 0) - (doc.paid_amount || 0))).toFixed(2));
  const markPoPaid = async (doc) => {
    const bal = poBalance(doc);
    if (bal <= 0) return;
    if (!window.confirm(`Record ${fmtAmt(bal)} paid to ${doc.vendor_name} today against ${doc.po_number}? A vendor payment entry will be created under Payments → Vendor Payments.`)) return;
    try {
      await axios.post(`${API}/ledger/vendor-payments`, { project_id: projectId, po_id: doc.id, vendor_name: doc.vendor_name, amount: bal, date: todayIST(), mode: "NEFT", reference: "", notes: "Marked as paid from the PO page", tds_amount: 0 });
      toast.success(`${doc.po_number} marked as paid`);
      refreshPo(doc.id);
    } catch (err) { toast.error(err.response?.data?.detail || "Could not record the payment"); }
  };

  const markPayment = async (docId, status, date, amount) => {
    try {
      const { data } = await axios.patch(`${API}/financials/pi/${docId}/payment`, {
        payment_status: status,
        payment_date: date || undefined,
        paid_amount: amount || undefined
      });
      setDocuments(p => p.map(d => d.id === docId ? data : d));
      toast.success(status === "received" ? "Receipt recorded — see Payments → Customer Payments" : "Payment status updated");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to update payment"); }
  };

  const openPrintView = (rawDoc) => {
    // Everything interpolated into the print HTML is escaped first: a vendor named
    // "<img onerror=…>" must print as text, not run with the app's cookies.
    const doc = escapeDeep(rawDoc);
    const ws = escapeDeep(workspace || {});
    const docType = isPO ? "PURCHASE ORDER" : "PROFORMA INVOICE";
    const numberField = isPO ? doc.po_number : doc.pi_number;

    const printLines = doc.composite_lines?.length ? doc.composite_lines : (doc.items || []);
    const itemsRows = printLines.map((item, i) => `
      <tr style="background:${i % 2 === 0 ? "#fff" : "#f8fafc"}">
        <td style="padding:6px 8px;text-align:center">${i + 1}</td>
        <td style="padding:6px 8px">${item.description || ""}</td>
        <td style="padding:6px 8px;text-align:center">${item.hsn_code || ""}</td>
        <td style="padding:6px 8px;text-align:center">${item.quantity}</td>
        <td style="padding:6px 8px;text-align:center">${item.unit}</td>
        <td style="padding:6px 8px;text-align:right">${(item.rate || 0).toLocaleString("en-IN", {minimumFractionDigits:2})}</td>
        <td style="padding:6px 8px;text-align:center">${(item.gst_rate_applied ?? item.gst_rate) != null ? `${item.gst_rate_applied ?? item.gst_rate}%` : `${(doc.cgst_rate || 0) + (doc.sgst_rate || 0) + (doc.igst_rate || 0)}%`}</td>
        <td style="padding:6px 8px;text-align:right;font-weight:600">${(item.amount || 0).toLocaleString("en-IN", {minimumFractionDigits:2})}</td>
      </tr>`).join("");

    const money = (n) => `₹ ${(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;
    const taxRow = (label, amt, red) => `<tr><td colspan="6"></td><td style="padding:4px 8px;text-align:right;white-space:nowrap">${label}</td><td style="padding:4px 8px;text-align:right${red ? ";color:#dc2626" : ""}">${red ? "- " : ""}${money(amt)}</td></tr>`;
    const breakup = (doc.gst_breakup || []).filter(b => b.taxable > 0);
    const gstRows = doc.mixed_gst && breakup.length
      ? breakup.map(b => [
          b.cgst > 0 ? taxRow(`CGST @ ${b.rate / 2}% on ${money(b.taxable)}`, b.cgst) : "",
          b.sgst > 0 ? taxRow(`SGST @ ${b.rate / 2}% on ${money(b.taxable)}`, b.sgst) : "",
          b.igst > 0 ? taxRow(`IGST @ ${b.rate}% on ${money(b.taxable)}`, b.igst) : "",
        ].join("")).join("")
      : [
          doc.cgst > 0 ? taxRow(`CGST @ ${doc.cgst_rate}%`, doc.cgst) : "",
          doc.sgst > 0 ? taxRow(`SGST @ ${doc.sgst_rate}%`, doc.sgst) : "",
          doc.igst > 0 ? taxRow(`IGST @ ${doc.igst_rate}%`, doc.igst) : "",
        ].join("");
    const taxRows = gstRows + ((!isPO && doc.tds_amount > 0) ? taxRow(`TDS @ ${doc.tds_rate}%`, doc.tds_amount, true) : "");

    const rightCol = isPO
      ? `<div style="font-size:11px;color:#475569"><strong>Vendor:</strong> ${doc.vendor_name || ""}<br>${doc.vendor_address ? doc.vendor_address.replace(/\n/g,"<br>") : ""}<br>${doc.vendor_gstin ? `<strong>GSTIN:</strong> ${doc.vendor_gstin}` : ""}</div>`
      : `<div style="font-size:11px;color:#475569"><strong>Bill To:</strong><br>${(doc.bill_to || "").replace(/\n/g,"<br>")}<br><br><strong>Milestone:</strong> ${doc.milestone || "N/A"}</div>`;

    const bankSection = ws.bank_name ? `
      <div style="margin-top:24px;padding:12px;background:#f8fafc;border:1px solid #e2e8f0;border-radius:6px;font-size:11px">
        <strong>Bank Details:</strong><br>
        Bank: ${ws.bank_name} &nbsp;|&nbsp; A/C No: ${ws.bank_account || ""} &nbsp;|&nbsp; IFSC: ${ws.bank_ifsc || ""} &nbsp;|&nbsp; Branch: ${ws.bank_branch || ""}
      </div>` : "";

    const termsSection = (doc.payment_terms || doc.terms_conditions) ? `
      <div style="margin-top:16px;font-size:11px;color:#475569">
        ${doc.payment_terms ? `<p><strong>Payment Terms:</strong> ${doc.payment_terms}</p>` : ""}
        ${doc.terms_conditions ? `<p><strong>Terms & Conditions:</strong> ${doc.terms_conditions}</p>` : ""}
      </div>` : "";

    const baseUrl = process.env.REACT_APP_BACKEND_URL || "";
    const html = `<!DOCTYPE html><html><head>
      <meta charset="UTF-8">
      <title>${docType} - ${numberField}</title>
      <style>
        * { margin:0; padding:0; box-sizing:border-box; }
        body { font-family: 'Segoe UI', Arial, sans-serif; font-size:12px; color:#1e293b; background:#fff; }
        .page { max-width:800px; margin:0 auto; padding:32px; }
        table { width:100%; border-collapse:collapse; }
        @media print {
          body { padding:0; }
          .no-print { display:none; }
          .page { padding:16px; max-width:100%; }
          @page { margin:15mm; }
        }
      </style>
    </head><body>
      <div class="page">
        <div class="no-print" style="margin-bottom:20px;text-align:right">
          <button onclick="window.print()" style="background:#FFB703;border:none;padding:8px 20px;font-size:13px;font-weight:600;cursor:pointer;border-radius:4px">Print / Save as PDF</button>
        </div>
        <div style="display:flex;justify-content:space-between;align-items:flex-start;padding:16px;background:#0A1128;color:white;border-radius:6px 6px 0 0;margin-bottom:0">
          <div style="display:flex;align-items:center;gap:12px">
            ${ws.logo_url ? `<img src="${baseUrl}${ws.logo_url}" alt="logo" style="height:44px;width:auto;object-fit:contain;background:white;padding:3px;border-radius:3px">` : ""}
            <div>
              <div style="font-size:18px;font-weight:700">${ws.name || "Company Name"}</div>
              ${ws.gstin ? `<div style="font-size:11px;color:#94a3b8;margin-top:2px">GSTIN: ${ws.gstin}</div>` : ""}
              ${ws.address ? `<div style="font-size:11px;color:#94a3b8;margin-top:2px">${ws.address}</div>` : ""}
            </div>
          </div>
          <div style="text-align:right">
            <div style="font-size:20px;font-weight:800;color:#FFB703">${docType}</div>
            <div style="font-size:14px;font-weight:600;margin-top:4px">${numberField}</div>
            <div style="font-size:11px;color:#94a3b8;margin-top:2px">Date: ${doc.date || ""}</div>
          </div>
        </div>
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:0;border:1px solid #e2e8f0;border-top:3px solid #FFB703">
          <div style="padding:12px;border-right:1px solid #e2e8f0;font-size:11px;color:#475569">
            <strong style="display:block;margin-bottom:4px;font-size:10px;text-transform:uppercase;letter-spacing:0.05em;color:#94a3b8">From (Our Company)</strong>
            <strong>${ws.name || ""}</strong><br>
            ${ws.address ? ws.address.replace(/\n/g,"<br>") : ""}
            ${ws.gstin ? `<br>GSTIN: ${ws.gstin}` : ""}
          </div>
          <div style="padding:12px;font-size:11px;color:#475569">
            <strong style="display:block;margin-bottom:4px;font-size:10px;text-transform:uppercase;letter-spacing:0.05em;color:#94a3b8">${isPO ? "To (Vendor)" : "To (Client)"}</strong>
            ${rightCol}
          </div>
        </div>
        <div style="margin-top:16px">
          <table style="border:1px solid #e2e8f0;font-size:11px">
            <thead>
              <tr style="background:#0A1128;color:white">
                <th style="padding:8px;text-align:center;width:40px">#</th>
                <th style="padding:8px;text-align:left">Description</th>
                <th style="padding:8px;text-align:center;width:60px">HSN</th>
                <th style="padding:8px;text-align:center;width:50px">Qty</th>
                <th style="padding:8px;text-align:center;width:50px">Unit</th>
                <th style="padding:8px;text-align:right;width:80px">Rate (₹)</th>
                <th style="padding:8px;text-align:center;width:50px">GST</th>
                <th style="padding:8px;text-align:right;width:90px">Amount (₹)</th>
              </tr>
            </thead>
            <tbody>
              ${itemsRows}
              <tr style="border-top:2px solid #e2e8f0">
                <td colspan="6"></td>
                <td style="padding:6px 8px;text-align:right;font-weight:600">Subtotal</td>
                <td style="padding:6px 8px;text-align:right;font-weight:600">₹ ${(doc.subtotal||0).toLocaleString("en-IN",{minimumFractionDigits:2})}</td>
              </tr>
              ${taxRows}
              <tr style="background:#fef3c7">
                <td colspan="6"></td>
                <td style="padding:8px;text-align:right;font-weight:700;font-size:13px">TOTAL</td>
                <td style="padding:8px;text-align:right;font-weight:700;font-size:13px;color:#0A1128">₹ ${(doc.total||0).toLocaleString("en-IN",{minimumFractionDigits:2})}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div style="margin-top:12px;padding:8px 12px;background:#f8fafc;border:1px solid #e2e8f0;border-radius:4px;font-size:11px;font-style:italic;color:#475569">
          <strong>Amount in Words:</strong> ${doc.amount_in_words || ""}
        </div>
        ${termsSection}
        ${bankSection}
        <div style="margin-top:40px;display:flex;justify-content:flex-end">
          <div style="text-align:center;border-top:1px solid #cbd5e1;padding-top:8px;width:160px;font-size:11px;color:#475569">
            <strong>${ws.authorized_signatory || "Authorized Signatory"}</strong><br>
            ${ws.name || ""}
          </div>
        </div>
      </div>
    </body></html>`;

    openHtmlInNewTab(html);
  };

  const formatINR = (n) => `₹${(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  if (loading) return <div className="p-8 text-[#64748B]">Loading...</div>;

  return (
    <div className="p-6 animate-fade-up">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-xs text-[#64748B] mb-4">
        <Link to="/app/projects" className="hover:text-[#FFB703]">Projects</Link>
        <ChevronRight size={12} />
        <Link to={`/app/projects/${projectId}`} className="hover:text-[#FFB703]">{project?.name}</Link>
        <ChevronRight size={12} />
        <span className="text-[#0A1128] font-medium">{isPO ? "Purchase Orders" : "Proforma Invoices"}</span>
      </div>

      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading font-bold text-xl text-[#0A1128]">
          {isPO ? "Purchase Order Generator" : "Proforma Invoice Generator"}
        </h1>
        {editDocId && (
          <button onClick={cancelEdit} className="flex items-center gap-1.5 text-xs text-[#64748B] border border-[#E2E8F0] px-3 py-1.5 rounded hover:bg-[#F8FAFC]">
            <X size={12} /> Cancel Edit
          </button>
        )}
      </div>

      {milestoneDraft && !editDocId && (
        <div className="mb-4 flex items-center gap-2 bg-[#E6F4EC] border border-[#2F7D52]/30 rounded px-4 py-2" data-testid="milestone-banner">
          <Receipt size={13} className="text-[#2F7D52]" />
          <span className="text-sm text-[#1B2B22]">Pre-filled from milestone <strong>{milestoneDraft.milestone}</strong> — check the amount and GST, then create the invoice. It will be linked to the milestone.</span>
        </div>
      )}
      {editDocId && (
        <div className="mb-4 flex items-center gap-2 bg-amber-50 border border-amber-200 rounded px-4 py-2">
          <Pencil size={13} className="text-amber-600" />
          <span className="text-sm text-amber-700 font-medium">Editing existing {isPO ? "PO" : "PI"} — changes will update the saved document.</span>
        </div>
      )}

      <div className={`grid grid-cols-1 ${canEditDoc ? "xl:grid-cols-3" : ""} gap-6`}>
        {/* Form */}
        {canEditDoc && <div className="xl:col-span-2 space-y-4">
          {/* Company from / to */}
          <div className="bg-white border border-[#E2E8F0] rounded p-5">
            <h3 className="font-heading font-semibold text-sm text-[#0A1128] mb-4">
              {isPO ? "Vendor Details" : "Client Details"}
            </h3>
            <div className="grid grid-cols-2 gap-4">
              {isPO ? (
                <>
                  <div className="col-span-2">
                    <label className="block text-xs text-[#64748B] mb-1">Vendor Name *</label>
                    <input type="text" list="vendor-master-list" value={form.vendor_name} onChange={e => {
                      const v = e.target.value;
                      const match = masters.find(m => m.name.toLowerCase() === v.toLowerCase());
                      if (match) {
                        setForm(p => ({ ...p, vendor_name: match.name, vendor_address: match.address || p.vendor_address, vendor_gstin: match.gstin || p.vendor_gstin, payment_terms: match.default_payment_terms || p.payment_terms }));
                      } else {
                        setForm(p => ({ ...p, vendor_name: v }));
                      }
                    }} placeholder="Start typing — pick a saved vendor or add new" className="w-full border border-[#E2E8F0] rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#FFB703]" data-testid="po-vendor-name" />
                    <datalist id="vendor-master-list">
                      {masters.map(m => <option key={m.id} value={m.name}>{m.gstin || ""}</option>)}
                    </datalist>
                    {masters.length > 0 && <p className="text-[10px] text-[#2F7D52] mt-1">{masters.length} saved vendor(s) — pick one to auto-fill</p>}
                  </div>
                  <div>
                    <label className="block text-xs text-[#64748B] mb-1">Vendor Address</label>
                    <textarea value={form.vendor_address} onChange={e => setForm(p => ({ ...p, vendor_address: e.target.value }))} rows={2} className="w-full border border-[#E2E8F0] rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#FFB703] resize-none" />
                  </div>
                  <div>
                    <label className="block text-xs text-[#64748B] mb-1">Vendor GSTIN</label>
                    <input type="text" value={form.vendor_gstin} onChange={handleVendorGstinChange} maxLength={15} placeholder="27AAAAA0000A1Z5" className="w-full border border-[#E2E8F0] rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#FFB703] font-mono" />
                    {form.vendor_gstin?.length >= 2 && workspace?.gstin && (
                      <p className="text-[10px] text-[#94A3B8] mt-1">
                        {form.vendor_gstin.slice(0,2) === workspace.gstin.slice(0,2)
                          ? "Same state → CGST + SGST applied"
                          : "Different state → IGST applied"}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs text-[#64748B] mb-1">PO Date</label>
                    <input type="date" value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))} className="w-full border border-[#E2E8F0] rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#FFB703]" />
                  </div>
                  <div>
                    <label className="block text-xs text-[#64748B] mb-1">Payment Terms</label>
                    <input type="text" value={form.payment_terms} onChange={e => setForm(p => ({ ...p, payment_terms: e.target.value }))} placeholder="50% advance, 50% on delivery" className="w-full border border-[#E2E8F0] rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#FFB703]" />
                  </div>
                </>
              ) : (
                <>
                  <div className="col-span-2">
                    <label className="block text-xs text-[#64748B] mb-1">Select Client (auto-fills bill-to & GSTIN)</label>
                    <input type="text" list="client-master-list" onChange={e => {
                      const match = masters.find(m => m.name.toLowerCase() === e.target.value.toLowerCase());
                      if (match) setForm(p => ({ ...p, bill_to: [match.name, match.billing_address].filter(Boolean).join("\n") || p.bill_to, ship_to: match.ship_to_address || p.ship_to, client_gstin: match.gstin || p.client_gstin }));
                    }} placeholder="Start typing a saved client…" className="w-full border border-[#E2E8F0] rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#FFB703]" data-testid="pi-client-select" />
                    <datalist id="client-master-list">
                      {masters.map(m => <option key={m.id} value={m.name}>{m.gstin || ""}</option>)}
                    </datalist>
                    {masters.length > 0 && <p className="text-[10px] text-[#2F7D52] mt-1">{masters.length} saved client(s)</p>}
                  </div>
                  <div>
                    <label className="block text-xs text-[#64748B] mb-1">Bill To *</label>
                    <textarea value={form.bill_to} onChange={e => setForm(p => ({ ...p, bill_to: e.target.value }))} rows={2} placeholder={project?.client_name} className="w-full border border-[#E2E8F0] rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#FFB703] resize-none" />
                  </div>
                  <div>
                    <label className="block text-xs text-[#64748B] mb-1">Ship To (Site)</label>
                    <textarea value={form.ship_to} onChange={e => setForm(p => ({ ...p, ship_to: e.target.value }))} rows={2} placeholder={project?.location} className="w-full border border-[#E2E8F0] rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#FFB703] resize-none" />
                  </div>
                  <div>
                    <label className="block text-xs text-[#64748B] mb-1">Client GSTIN</label>
                    <input type="text" value={form.client_gstin || ""} onChange={e => setForm(p => ({ ...p, client_gstin: e.target.value.toUpperCase() }))} maxLength={15} placeholder="24AAAAA0000A1Z5" className="w-full border border-[#E2E8F0] rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#FFB703] font-mono" data-testid="pi-client-gstin" />
                    {form.client_gstin?.length >= 2 && workspace?.gstin && (
                      <p className="text-[10px] text-[#2F7D52] mt-1">
                        {form.client_gstin.slice(0,2) === workspace.gstin.slice(0,2)
                          ? "Intra-state → CGST + SGST (auto)"
                          : "Inter-state → IGST (auto)"}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs text-[#64748B] mb-1">Milestone</label>
                    <input type="text" value={form.milestone} onChange={e => setForm(p => ({ ...p, milestone: e.target.value }))} placeholder="e.g. Phase 7 Commissioning" className="w-full border border-[#E2E8F0] rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#FFB703]" />
                  </div>
                  <div>
                    <label className="block text-xs text-[#64748B] mb-1">Invoice Date</label>
                    <input type="date" value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))} className="w-full border border-[#E2E8F0] rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#FFB703]" />
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Line Items */}
          <div className="bg-white border border-[#E2E8F0] rounded p-5">
            <h3 className="font-heading font-semibold text-sm text-[#0A1128] mb-4">Line Items</h3>
            <LineItemsTable items={form.items} setItems={items => setForm(p => ({ ...p, items }))} defaultRate={taxes.defaultRate} catalogue={catalogue} />
          </div>

          {/* GST / Tax */}
          <div className="bg-white border border-[#E2E8F0] rounded p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-semibold text-sm text-[#0A1128]">Tax & Summary</h3>
              <div className="flex items-center gap-3 flex-wrap">
                {!isPO && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-[#64748B]">Tax treatment:</span>
                    <select value={form.tax_treatment || "services_18"} onChange={e => setForm(p => ({ ...p, tax_treatment: e.target.value }))}
                      className="border border-[#E2E8F0] rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-[#FFB703] bg-white max-w-[320px]" data-testid="pi-tax-treatment">
                      {TAX_TREATMENTS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                    </select>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <span className="text-xs text-[#64748B]" title="Applies to lines whose GST % is set to “default”">Default GST:</span>
                  <select value={gstRate} onChange={e => { const r = +e.target.value; setGstRate(r); updateTaxRates(undefined, r); }}
                    className="border border-[#E2E8F0] rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-[#FFB703] bg-white">
                    {[5, 12, 18, 28].map(r => <option key={r} value={r}>{r}%</option>)}
                  </select>
                </div>
                <div className="flex bg-[#F1F5F9] rounded p-0.5 gap-0.5">
                  {[["auto","Auto"],["cgst_sgst","CGST+SGST"],["igst","IGST"]].map(([k,l]) => (
                    <button key={k} type="button" onClick={() => { setTaxMode(k); if (k !== "auto") updateTaxRates(k, undefined); }}
                      className={`px-2 py-1 rounded text-[10px] font-semibold transition-all ${taxMode===k?"bg-white text-[#0A1128] shadow":"text-[#64748B]"}`}>
                      {l}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            {isComposite && (
              <div className="mb-4 rounded border border-[#FDE68A] bg-[#FFFBEB] p-3 text-xs text-[#92400E]" data-testid="composite-preview">
                <div className="font-semibold mb-1">Solar composite 70:30 — tax is computed on these two legs per line (shown on the PI and PDF):</div>
                <table className="w-full text-[11px]"><tbody>
                  {taxLines.map((l, i) => <tr key={i}><td className="py-0.5 pr-2">{l.description}</td><td className="py-0.5 pr-2 text-center">{l.hsn_code}</td><td className="py-0.5 pr-2 text-right">{formatINR(l.amount)}</td><td className="py-0.5 text-right font-semibold">@ {l.gst_rate}%</td></tr>)}
                </tbody></table>
                <div className="mt-1">Effective GST {subtotal ? ((cgst + sgst + igst) / subtotal * 100).toFixed(2) : "0.00"}% · place of supply follows the project site state (set in Edit Details); use CGST+SGST / IGST to override.</div>
              </div>
            )}
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="bg-[#F8FAFC] rounded p-3">
                <div className="text-[10px] text-[#94A3B8] uppercase font-semibold mb-1">CGST</div>
                <div className="text-sm font-bold text-[#0A1128]">{taxes.mixed ? "mixed" : `${taxes.inter ? 0 : taxes.docRate / 2}%`}</div>
                <div className="text-xs text-[#64748B]">{formatINR(cgst)}</div>
              </div>
              <div className="bg-[#F8FAFC] rounded p-3">
                <div className="text-[10px] text-[#94A3B8] uppercase font-semibold mb-1">SGST</div>
                <div className="text-sm font-bold text-[#0A1128]">{taxes.mixed ? "mixed" : `${taxes.inter ? 0 : taxes.docRate / 2}%`}</div>
                <div className="text-xs text-[#64748B]">{formatINR(sgst)}</div>
              </div>
              <div className="bg-[#F8FAFC] rounded p-3">
                <div className="text-[10px] text-[#94A3B8] uppercase font-semibold mb-1">IGST</div>
                <div className="text-sm font-bold text-[#0A1128]">{taxes.mixed ? "mixed" : `${taxes.inter ? taxes.docRate : 0}%`}</div>
                <div className="text-xs text-[#64748B]">{formatINR(igst)}</div>
              </div>
              {!isPO && (
                <div className="col-span-3 flex items-center gap-3">
                  <input type="checkbox" id="tds" checked={form.tds_applicable} onChange={e => setForm(p => ({ ...p, tds_applicable: e.target.checked }))} className="w-4 h-4" />
                  <label htmlFor="tds" className="text-sm text-[#64748B]">TDS Applicable</label>
                  {form.tds_applicable && (
                    <input type="number" value={form.tds_rate} onChange={e => setForm(p => ({ ...p, tds_rate: +e.target.value }))} className="w-20 border border-[#E2E8F0] rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-[#FFB703]" placeholder="%" />
                  )}
                </div>
              )}
            </div>
            <div className="border-t border-[#E2E8F0] pt-4 space-y-1">
              <div className="flex justify-between text-sm"><span className="text-[#64748B]">Subtotal</span><span className="tabular-nums">{formatINR(subtotal)}</span></div>
              {taxes.mixed ? taxes.breakup.filter(b => b.taxable > 0).map(b => (
                <React.Fragment key={b.rate}>
                  {b.cgst > 0 && <div className="flex justify-between text-sm"><span className="text-[#64748B]">CGST {b.rate / 2}% on {formatINR(b.taxable)}</span><span className="tabular-nums">{formatINR(b.cgst)}</span></div>}
                  {b.sgst > 0 && <div className="flex justify-between text-sm"><span className="text-[#64748B]">SGST {b.rate / 2}% on {formatINR(b.taxable)}</span><span className="tabular-nums">{formatINR(b.sgst)}</span></div>}
                  {b.igst > 0 && <div className="flex justify-between text-sm"><span className="text-[#64748B]">IGST {b.rate}% on {formatINR(b.taxable)}</span><span className="tabular-nums">{formatINR(b.igst)}</span></div>}
                </React.Fragment>
              )) : <>
              {cgst > 0 && <div className="flex justify-between text-sm"><span className="text-[#64748B]">CGST ({taxes.docRate / 2}%)</span><span className="tabular-nums">{formatINR(cgst)}</span></div>}
              {sgst > 0 && <div className="flex justify-between text-sm"><span className="text-[#64748B]">SGST ({taxes.docRate / 2}%)</span><span className="tabular-nums">{formatINR(sgst)}</span></div>}
              {igst > 0 && <div className="flex justify-between text-sm"><span className="text-[#64748B]">IGST ({taxes.docRate}%)</span><span className="tabular-nums">{formatINR(igst)}</span></div>}
              </>}
              {taxes.mixed && <div className="text-[10px] text-[#2F7D52]" data-testid="mixed-gst-note">Line-item-wise GST: {taxes.breakup.filter(b => b.taxable > 0).map(b => `${b.rate}%`).join(", ")}</div>}
              {tds > 0 && <div className="flex justify-between text-sm"><span className="text-[#64748B]">TDS ({form.tds_rate}%)</span><span className="tabular-nums text-red-600">- {formatINR(tds)}</span></div>}
              <div className="flex justify-between font-bold text-base pt-2 border-t border-[#E2E8F0]">
                <span>Total</span>
                <span className="text-[#FFB703] tabular-nums">{formatINR(total)}</span>
              </div>
            </div>
          </div>

          <button
            onClick={handleSave}
            disabled={saving}
            className="w-full bg-[#FFB703] text-[#0A1128] font-bold py-3 rounded text-sm hover:bg-[#E8A200] transition-colors active:scale-[0.98] disabled:opacity-60 flex items-center justify-center gap-2"
            data-testid="save-document-btn"
          >
            {saving ? "Saving..." : editDocId ? <><Check size={14} />Update {isPO ? "Purchase Order" : "Proforma Invoice"}</> : `Create ${isPO ? "Purchase Order" : "Proforma Invoice"}`}
          </button>
        </div>}

        {/* Documents list */}
        <div className="space-y-4">
          <div className="bg-white border border-[#E2E8F0] rounded p-5">
            <h3 className="font-heading font-semibold text-sm text-[#0A1128] mb-4">
              Saved {isPO ? "POs" : "PIs"} ({documents.length})
            </h3>
            <div className="space-y-2">
              {documents.map(doc => (
                <div key={doc.id} className={`border rounded p-3 transition-colors ${editDocId === doc.id ? "border-[#FFB703] bg-amber-50" : "border-[#E2E8F0] hover:border-[#FFB703]/50"}`}>
                  <div className="flex items-start justify-between mb-1">
                    <span className="text-xs font-semibold text-[#0A1128]">{doc[isPO ? "po_number" : "pi_number"]}</span>
                    {doc.status === "void" ? (
                      <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-red-100 text-red-700" title={doc.void_reason || ""} data-testid={`void-badge-${doc.id}`}>Void</span>
                    ) : !isPO ? (
                      <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full ${doc.payment_status === "received" ? "bg-emerald-100 text-emerald-700" : doc.payment_status === "partial" ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-600"}`}>
                        {doc.payment_status === "received" ? "Paid" : doc.payment_status === "partial" ? "Partial" : "Pending"}
                      </span>
                    ) : (
                      <span className="flex items-center gap-1">
                        <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded ${doc.status === "sent" ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-600"}`}>{doc.status}</span>
                        <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full ${doc.payment_status === "paid" ? "bg-emerald-100 text-emerald-700" : doc.payment_status === "overpaid" ? "bg-purple-100 text-purple-700" : doc.payment_status === "partial" ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-600"}`} data-testid={`po-pay-badge-${doc.id}`}>
                          {doc.payment_status === "paid" ? "Paid" : doc.payment_status === "overpaid" ? "Overpaid" : doc.payment_status === "partial" ? "Part-paid" : "Unpaid"}
                        </span>
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-[#64748B] mb-2">
                    {isPO ? doc.vendor_name : doc.bill_to?.split("\n")[0]}
                  </div>
                  {doc.gst_note && <div className="text-[10px] text-[#2F7D52] mb-2" data-testid={`gst-note-${doc.id}`}>GST: {doc.gst_note}</div>}
                  {isPO && doc.status !== "void" && (doc.paid_amount > 0) && (
                    <div className="text-[10px] text-[#64748B] mb-2">Paid {fmtAmt(doc.paid_amount)}{doc.tds_deducted > 0 ? ` (incl. TDS ${fmtAmt(doc.tds_deducted)})` : ""} · balance <span className={poBalance(doc) > 0 ? "text-[#B45309] font-semibold" : "text-emerald-700 font-semibold"}>{fmtAmt(poBalance(doc))}</span></div>
                  )}
                  {doc.status === "void" && doc.void_reason && <div className="text-[10px] text-red-600 mb-2">Voided: {doc.void_reason}</div>}
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-bold text-[#0A1128]">₹{doc.total?.toLocaleString("en-IN")}</span>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setExpandedDocId(expandedDocId === doc.id ? null : doc.id)}
                        className="flex items-center gap-1 text-xs font-medium text-[#64748B] hover:text-blue-600 transition-colors"
                        data-testid={`view-items-${doc.id}`}
                        title="View line items"
                      >
                        {expandedDocId === doc.id ? <ChevronUp size={10} /> : <ChevronDown size={10} />}
                        Items
                      </button>
                      {canEditDoc && <button
                        onClick={() => startEdit(doc)}
                        disabled={doc.status === "void"}
                        className={`flex items-center gap-1 text-xs font-medium ${doc.status === "void" ? "text-slate-300 cursor-not-allowed" : editDocId === doc.id ? "text-amber-600" : "text-[#64748B] hover:text-[#0A1128]"}`}
                        data-testid={`edit-doc-${doc.id}`}
                        title={doc.status === "void" ? "Voided documents cannot be edited" : "Edit this document"}
                      >
                        <Pencil size={10} /> Edit
                      </button>}
                      {canVoidDoc && doc.status !== "void" && (
                        <button
                          onClick={() => voidDoc(doc)}
                          className="flex items-center gap-1 text-xs font-medium text-[#64748B] hover:text-red-600"
                          data-testid={`void-doc-${doc.id}`}
                          title="Void this document (keeps number, excludes from totals)"
                        >
                          <X size={10} /> Void
                        </button>
                      )}
                      <button
                        onClick={() => openPrintView(doc)}
                        className="flex items-center gap-1 text-xs text-[#FFB703] hover:text-[#E8A200] font-medium"
                        data-testid={`print-doc-${doc.id}`}
                      >
                        <Download size={10} /> Print
                      </button>
                    </div>
                  </div>
                  {/* Expandable line items view */}
                  {expandedDocId === doc.id && doc.items?.length > 0 && (
                    <div className="mt-2 border-t border-[#F1F5F9] pt-2">
                      <div className="overflow-x-auto rounded border border-[#E2E8F0]">
                        <table className="w-full text-[10px]">
                          <thead>
                            <tr className="bg-[#F1F5F9] text-[#64748B]">
                              <th className="px-2 py-1.5 text-left font-semibold">#</th>
                              <th className="px-2 py-1.5 text-left font-semibold">Description</th>
                              <th className="px-2 py-1.5 text-right font-semibold">Qty</th>
                              <th className="px-2 py-1.5 text-right font-semibold">Rate</th>
                              <th className="px-2 py-1.5 text-right font-semibold">GST</th>
                              <th className="px-2 py-1.5 text-right font-semibold">Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            {doc.items.map((item, idx) => (
                              <tr key={item.id || `doc-item-${idx}`} className="border-t border-[#F1F5F9]">
                                <td className="px-2 py-1.5 text-[#64748B]">{idx + 1}</td>
                                <td className="px-2 py-1.5 text-[#0A1128]">{item.description}</td>
                                <td className="px-2 py-1.5 text-right text-[#64748B]">{item.quantity} {item.unit}</td>
                                <td className="px-2 py-1.5 text-right text-[#64748B]">₹{Number(item.rate).toLocaleString("en-IN")}</td>
                                <td className="px-2 py-1.5 text-right text-[#64748B]">{(item.gst_rate_applied ?? item.gst_rate) != null ? `${item.gst_rate_applied ?? item.gst_rate}%` : `${((doc.cgst_rate || 0) + (doc.sgst_rate || 0) + (doc.igst_rate || 0))}%`}</td>
                                <td className="px-2 py-1.5 text-right font-semibold text-[#0A1128]">₹{Number(item.amount || (item.quantity * item.rate)).toLocaleString("en-IN")}</td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr className="border-t-2 border-[#E2E8F0] bg-[#F8FAFC]">
                              <td colSpan={5} className="px-2 py-1.5 text-right font-bold text-[#0A1128]">Total</td>
                              <td className="px-2 py-1.5 text-right font-bold text-[#0A1128]">₹{doc.total?.toLocaleString("en-IN")}</td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    </div>
                  )}
                  {isPO && canPayVendor && doc.status !== "void" && poBalance(doc) > 0 && (
                    <div className="pt-2 border-t border-[#F1F5F9] flex gap-2">
                      <button onClick={() => markPoPaid(doc)} title="Records the full outstanding balance as paid today"
                        className="flex-1 text-[10px] font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 py-1.5 rounded transition-colors" data-testid={`po-mark-paid-${doc.id}`}>
                        Mark as Paid ({fmtAmt(poBalance(doc))})
                      </button>
                      <button onClick={() => setPayPo(doc)} title="Record a part payment, TDS, cheque/UTR reference…"
                        className="flex-1 flex items-center justify-center gap-1 text-[10px] font-semibold text-[#1B2B22] bg-[#F1F5F9] hover:bg-[#E2E8F0] py-1.5 rounded transition-colors" data-testid={`po-record-payment-${doc.id}`}>
                        <Wallet size={10} /> Record payment
                      </button>
                    </div>
                  )}
                  {!isPO && canMarkPaid && doc.payment_status !== "received" && doc.status !== "void" && (
                    <div className="pt-2 border-t border-[#F1F5F9]">
                      <button
                        onClick={() => { if (window.confirm(`Record the outstanding ${fmtAmt(doc.balance ?? (doc.total - (doc.paid_amount || 0)))} as received today? A customer payment entry will be created.`)) markPayment(doc.id, "received", todayIST()); }}
                        title="Creates a customer payment for the outstanding balance"
                        className="w-full text-[10px] font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 py-1.5 rounded transition-colors"
                        data-testid={`mark-paid-${doc.id}`}
                      >
                        Mark as Received
                      </button>
                    </div>
                  )}
                  {!isPO && doc.payment_status === "received" && doc.payment_date && (
                    <div className="text-[10px] text-emerald-600 font-medium">
                      Received: {new Date(doc.payment_date).toLocaleDateString("en-IN")}
                    </div>
                  )}
                </div>
              ))}
              {!documents.length && (
                <p className="text-xs text-[#64748B] text-center py-4">No {isPO ? "POs" : "PIs"} created yet</p>
              )}
            </div>
          </div>

          {payPo && project && (
            <VendorPaymentModal projects={[project]} onClose={() => setPayPo(null)}
              initial={{ project_id: projectId, po_id: payPo.id, vendor_name: payPo.vendor_name, amount: poBalance(payPo), date: todayIST(), mode: "NEFT", reference: "", notes: "", tds_amount: "" }}
              onSaved={() => { setPayPo(null); refreshPo(payPo.id); toast.success(`Payment recorded against ${payPo.po_number}`); }} />
          )}
          {workspace && (
            <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded p-4">
              <div className="text-[10px] font-semibold text-[#64748B] uppercase tracking-wider mb-2">From (Your Company)</div>
              <div className="text-xs font-semibold text-[#0A1128]">{workspace.name}</div>
              {workspace.gstin && <div className="text-xs text-[#64748B]">GSTIN: {workspace.gstin}</div>}
              {workspace.address && <div className="text-xs text-[#64748B] mt-1">{workspace.address}</div>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
